import { useState, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useAccount, useSwitchChain } from 'wagmi';
import { useAppContext, useSelectedAccount, useSelectedBroker } from '../contexts/hooks';
import { useEthersSigner } from '../hooks/useEthersSigner.ts';
import { useSolanaSigner } from '../hooks/useSolanaSigner.ts';
import { useOffboardingActions } from '../hooks/useOffboardingActions.ts';
import { CHAIN_NAMES, getTokenInfo } from '../services/orderly-api';
import type { TokenInfo } from '../services/orderly-api';
import { config } from '../config.ts';
import { SUPPORTED_EVM_CHAIN_IDS } from '../wagmi.ts';
import { trackEvent } from '../lib/analytics';
import { formatFullWithdrawalAmount, getMaxWithdrawalAmounts, getNegativeUsdcBalance, getNegativeUsdcCollateralMessage, getWalletWithdrawalAmountError, isValidTokenDecimals, isWithdrawalAmountAboveAvailable, normalizeWithdrawalAmount } from '../lib/withdrawal';
import PositionTable from './PositionTable';
import ConfirmModal from './ConfirmModal';
import type { OffboardingStep, StepStatus, Account, AsyncStatus } from '../types';
import type { AccountScope } from '../contexts/AppContext';
import './StepProgress.css';
import { cn } from '@/lib/utils';
import { OdsAlert } from './orderly/OdsAlert';
import type { OdsAlertType } from './orderly/OdsAlert';
import { OdsBadge } from './orderly/OdsBadge';
import { OdsButton } from './orderly/OdsButton';
import { OdsInput } from './orderly/OdsInput';
import { OdsSelect } from './orderly/OdsSelect';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';
const MOCK_SCENARIO = import.meta.env.VITE_MOCK_SCENARIO as string | undefined;
const USE_NEGATIVE_USDC_MOCK = USE_MOCK && MOCK_SCENARIO === 'negative-usdc';
const SWITCH_CHAIN_TIMEOUT_MS = 15000;
const ABSTRACT_CONNECTOR_ID = 'xyz.abs.privy';
const ABSTRACT_CHAIN_IDS = new Set([2741, 11124]);
const DEFAULT_CONVERT_SLIPPAGE = 0.01;
const CONVERT_COVER_BUFFER = 0.012;
const SOLANA_CHAIN_ID = 900900900;

interface StepConfig {
  key: OffboardingStep;
  label: string;
  subLabel: string;
  skippedLabel: string;
}

interface ConvertOptionItem {
  token: string;
  amount: number;
  available: number;
  price: number;
  estimatedUsdc: number;
  canCoverTarget: boolean;
}

interface WithdrawalAmountInput {
  value: string;
  source: 'auto' | 'manual';
}

function formatUSD(val: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(val);
}

function formatUSDPrecise(val: number): string {
  // Round to 6 decimal places
  const rounded = Math.round(val * 1000000) / 1000000;
  // Format with Intl
  let formatted = new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 6 
  }).format(rounded);
  // Remove trailing zeros from decimal part
  formatted = formatted.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  return formatted;
}

function formatNum(val: number, decimals = 6): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: decimals }).format(val);
}

function formatMarginMode(mode: Account['positions'][number]['marginMode']): string {
  return mode === 'ISOLATED' ? 'ISO' : 'Cross';
}

function formatAmountInput(val: number): string {
  return Number.isFinite(val) ? String(val) : '';
}

function parseAmountInput(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function getAvailableAmount(holding: { holding: number; frozen?: number }): number {
  return Math.max(0, holding.holding - (holding.frozen ?? 0));
}

function hasNegativeUsdc(account: Account): boolean {
  return getNegativeUsdcBalance(account) > 0;
}

function getAmountValidationError(value: string, available: number, decimals?: number): string | null {
  if (decimals !== undefined) {
    try {
      normalizeWithdrawalAmount(value, decimals);
      if (isWithdrawalAmountAboveAvailable(value, available, decimals)) {
        return 'Amount exceeds available balance';
      }
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : 'Enter a valid amount';
    }
  }
  const parsed = parseAmountInput(value);
  if (parsed === null) return 'Enter an amount';
  if (parsed <= 0) return 'Amount must be greater than 0';
  if (parsed > available) return 'Amount exceeds available balance';
  return null;
}

function sortChainOptions<T extends { label: string }>(chains: T[]): T[] {
  return [...chains].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

function getChainLabel(chainId: number | string): string {
  const chainKey = String(chainId);
  if (chainKey === '11124') return 'Abstract Testnet';
  return CHAIN_NAMES[chainKey] || `Chain ${chainKey}`;
}

function isSupportedEvmDestinationChain(chainId: number | null): chainId is number {
  return chainId !== null && SUPPORTED_EVM_CHAIN_IDS.has(chainId);
}

function isDestinationChainForWallet(chainId: number | string, chainType: 'evm' | 'solana' | null): boolean {
  const numericChainId = Number(chainId);
  return chainType === 'solana' ? numericChainId === SOLANA_CHAIN_ID : numericChainId !== SOLANA_CHAIN_ID;
}

async function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timeoutId: number | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(message)), ms);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  }
}

function extractIndexPriceEntries(payload: unknown): Array<{ symbol: string; price: number }> {
  const entries: Array<{ symbol: string; price: number }> = [];

  const walk = (node: unknown) => {
    if (!node || typeof node !== 'object') return;

    if (Array.isArray(node)) {
      for (const item of node) walk(item);
      return;
    }

    const obj = node as Record<string, unknown>;
    const symbol = typeof obj.symbol === 'string' ? obj.symbol : null;
    const parsePrice = (value: unknown): number | null => {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
      }
      return null;
    };

    const rawPrice =
      parsePrice(obj.index_price) ??
      parsePrice(obj.price) ??
      parsePrice(obj.indexPrice);

    if (symbol && rawPrice !== null) {
      entries.push({ symbol, price: rawPrice });
    }

    for (const value of Object.values(obj)) {
      walk(value);
    }
  };

  walk(payload);
  return entries;
}

function getSteps(isSubAccount: boolean): StepConfig[] {
  return [
    { key: 'cancel_orders', label: 'Cancel pending orders', subLabel: 'Cancel all open orders', skippedLabel: 'No pending orders' },
    { key: 'close_positions', label: 'Close all positions', subLabel: 'Place market orders to close', skippedLabel: 'No open positions' },
    { key: 'settle_pnl', label: 'Settle pending PnL', subLabel: 'Finalize and convert your pending PnL into your account balance', skippedLabel: 'No unsettled PnL' },
    {
      key: 'convert_negative_usdc',
      label: 'Convert to cover negative USDC',
      subLabel: 'Convert collateral assets into USDC before withdrawal',
      skippedLabel: 'No negative USDC to cover',
    },
    {
      key: 'withdraw',
      label: isSubAccount ? 'Transfer to main account' : 'Withdraw to wallet',
      subLabel: isSubAccount ? 'Move assets to main account' : 'Withdraw assets to your wallet',
      skippedLabel: 'No holdings to withdraw',
    },
  ];
}

function isStepDone(s: StepStatus): boolean {
  return s === 'completed' || s === 'skipped';
}

function isStepEnabled(stepKey: OffboardingStep, steps: Record<OffboardingStep, StepStatus>): boolean {
  const order: OffboardingStep[] = ['cancel_orders', 'close_positions', 'settle_pnl', 'convert_negative_usdc', 'withdraw'];
  const idx = order.indexOf(stepKey);
  if (isStepDone(steps[stepKey])) return false;
  if (idx === 0) return steps[stepKey] === 'pending' || steps[stepKey] === 'error';
  return order.slice(0, idx).every((k) => isStepDone(steps[k])) && (steps[stepKey] === 'pending' || steps[stepKey] === 'error');
}

function hasPendingAction(step: OffboardingStep, account: Account | null): boolean {
  if (!account) return false;
  switch (step) {
    case 'cancel_orders':
      return account.pendingOrders && account.pendingOrders.length > 0;
    case 'close_positions':
      return account.positions && account.positions.length > 0;
    case 'settle_pnl':
      return account.unsettledPnl !== 0;
    case 'convert_negative_usdc':
      return hasNegativeUsdc(account);
    case 'withdraw':
      return (account.holdings ?? []).some((h: { holding: number }) => h.holding > 0);
    default:
      return false;
  }
}

function isSameAccountScope(left: AccountScope | null, right: AccountScope | null): boolean {
  return !!left && !!right
    && left.sessionGeneration === right.sessionGeneration
    && left.brokerId === right.brokerId
    && left.accountId === right.accountId;
}

interface StepProgressProps {
  onStepComplete?: () => void;
}

export default function StepProgress({ onStepComplete }: StepProgressProps) {
  const { state, dispatch } = useAppContext();
  const account = useSelectedAccount();
  const broker = useSelectedBroker();
  const evmSigner = useEthersSigner();
  const solanaSigner = useSolanaSigner();
  const { address: evmPublicAddress, chain, connector } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const [expandedStep, setExpandedStep] = useState<OffboardingStep | null>(null);
  const [withdrawToken, setWithdrawToken] = useState<string>('');
  const [withdrawAction, setWithdrawAction] = useState<'wallet' | 'transfer'>('wallet');
  const [selectedDestinationChain, setSelectedDestinationChain] = useState<number | null>(null);
  const [switchingDestinationChain, setSwitchingDestinationChain] = useState<number | null>(null);
  const [destinationChainSwitchError, setDestinationChainSwitchError] = useState<string | null>(null);
  const [transferTargetAccountId, setTransferTargetAccountId] = useState<string>('');
  const [withdrawAmounts, setWithdrawAmounts] = useState<Record<string, WithdrawalAmountInput>>({});
  const [confirmingStep, setConfirmingStep] = useState<OffboardingStep | null>(null);
  const [selectedConvertOption, setSelectedConvertOption] = useState<ConvertOptionItem | null>(null);
  // Track which asset card has its options expanded (token name or null)
  const [expandedAssetCard, setExpandedAssetCard] = useState<string | null>(null);
  const [tokenInfoMap, setTokenInfoMap] = useState<Map<string, TokenInfo>>(new Map());
  const [tokenMetadataStatus, setTokenMetadataStatus] = useState<AsyncStatus>('loading');
  const [tokenMetadataRetryNonce, setTokenMetadataRetryNonce] = useState(0);
  const [tokenPriceMap, setTokenPriceMap] = useState<Map<string, number>>(() => new Map([
    ['USDC', 1],
    ['USDT', 1],
    ...(USE_NEGATIVE_USDC_MOCK ? [
      ['BTC', 100000],
      ['ETH', 3500],
      ['SOL', 150],
      ['BNB', 700],
    ] as Array<[string, number]> : []),
  ]));
  const [failedTokenLogos, setFailedTokenLogos] = useState<Set<string>>(new Set());
  const [wsRetryNonce, setWsRetryNonce] = useState(0);
  const [successToast, setSuccessToast] = useState<{ message: string; type: OdsAlertType; label: string; origin: AccountScope } | null>(null);
  const successToastTimerRef = useRef<number | null>(null);
  const selectedScope: AccountScope | null = useMemo(() => account && broker
    ? { sessionGeneration: state.sessionGeneration, brokerId: broker.id, accountId: account.accountId }
    : null, [account, broker, state.sessionGeneration]);
  const selectedScopeRef = useRef<AccountScope | null>(selectedScope);
  selectedScopeRef.current = selectedScope;
  const brokerAuth = broker && state.authContext?.brokerId === broker.id ? state.authContext : null;
  const streamAccountId = brokerAuth?.accountId ?? account?.accountId;
  const trackedTokens = useMemo(
    () => Array.from(new Set((account?.holdings ?? []).map((h) => h.token))).sort(),
    [account?.holdings],
  );
  const trackedTokensKey = trackedTokens.join(',');
  const trimmedTransferTargetAccountId = transferTargetAccountId.trim();
  const isTransferTargetAccountIdValid = /^0x[a-fA-F0-9]{64}$/.test(trimmedTransferTargetAccountId);
  const isAbstractWallet = connector?.id === ABSTRACT_CONNECTOR_ID || connector?.name === 'Abstract';
  const selectedDestinationChainName = selectedDestinationChain !== null ? getChainLabel(selectedDestinationChain) : '';
  const destinationChainNeedsWalletSwitch = state.chain === 'evm'
    && isSupportedEvmDestinationChain(selectedDestinationChain)
    && chain?.id !== selectedDestinationChain;
  const destinationChainSelectMessage = switchingDestinationChain !== null
    ? `Switching wallet to ${getChainLabel(switchingDestinationChain)}...`
    : state.chain === 'evm' && isSupportedEvmDestinationChain(selectedDestinationChain) && !destinationChainNeedsWalletSwitch
      ? `Wallet network: ${selectedDestinationChainName}`
      : undefined;
  const destinationChainSelectError = destinationChainSwitchError
    ?? (destinationChainNeedsWalletSwitch ? `Switch your wallet to ${selectedDestinationChainName} before withdrawing.` : undefined);
  const signer = state.chain === 'solana' ? solanaSigner : evmSigner;
  const activeChainId = state.chain === 'solana' ? config.solana.chainId : chain?.id;
  const withdrawalDestinationChainId = selectedDestinationChain ?? activeChainId;
  const isAbstractWithdrawal = state.chain === 'evm'
    && isAbstractWallet
    && withdrawalDestinationChainId !== undefined
    && ABSTRACT_CHAIN_IDS.has(withdrawalDestinationChainId);
  const withdrawalReceiverAddress = isAbstractWithdrawal ? evmPublicAddress : state.walletAddress;
  const withdrawalAvailableMap = useMemo(
    () => account ? getMaxWithdrawalAmounts(account, tokenInfoMap, tokenPriceMap) : new Map<string, number>(),
    [account, tokenInfoMap, tokenPriceMap],
  );
  const { loadingStep, executeStep: executeLiveStep } = useOffboardingActions({
    account,
    broker,
    auth: brokerAuth,
    sessionGeneration: state.sessionGeneration,
    dispatch,
    signer,
    chainId: activeChainId,
    chainType: state.chain ?? undefined,
    withdrawalReceiverAddress,
    tokenInfoMap,
    withdrawalAvailableMap,
    onStepComplete,
  });

  useEffect(() => {
    if (selectedDestinationChain !== null && chain?.id === selectedDestinationChain) {
      setDestinationChainSwitchError(null);
    }
  }, [chain?.id, selectedDestinationChain]);

  useEffect(() => {
    if (!isAbstractWallet || state.chain !== 'evm') return;
    const abstractChainId = activeChainId !== undefined && ABSTRACT_CHAIN_IDS.has(activeChainId) ? activeChainId : 2741;
    if (selectedDestinationChain !== abstractChainId) {
      setSelectedDestinationChain(abstractChainId);
      setDestinationChainSwitchError(null);
    }
  }, [activeChainId, isAbstractWallet, selectedDestinationChain, state.chain]);

  useEffect(() => {
    trackEvent('component_viewed', {
      component: 'StepProgress',
      page: state.page,
      broker_id: broker?.id ?? undefined,
      account_id: account?.accountId ?? undefined,
      account_type: account?.type ?? undefined,
    });
  }, [state.page, broker?.id, account?.accountId, account?.type]);

  useEffect(() => {
    if (!confirmingStep || !account) return;
    trackEvent('modal_opened', {
      modal: 'confirm_execute_step',
      step: confirmingStep,
      component: 'StepProgress',
      account_id: account.accountId,
      account_type: account.type,
      broker_id: broker?.id ?? undefined,
      token: confirmingStep === 'convert_negative_usdc' ? selectedConvertOption?.token : undefined,
    });
  }, [confirmingStep, account, broker?.id, selectedConvertOption?.token]);

  useEffect(() => {
    return () => {
      if (successToastTimerRef.current) {
        window.clearTimeout(successToastTimerRef.current);
      }
    };
  }, []);

  // Fetch per-token supported chains
  useEffect(() => {
    let active = true;
    setTokenMetadataStatus('loading');
    setTokenInfoMap(new Map());
    getTokenInfo()
      .then((tokenInfo) => {
        if (!active) return;
        const infoMap = new Map<string, TokenInfo>();
        for (const t of tokenInfo.rows ?? []) {
          infoMap.set(t.token, { ...t, chain_details: Array.isArray(t.chain_details) ? t.chain_details : [] });
        }
        setTokenInfoMap(infoMap);
        setTokenMetadataStatus('success');
      })
      .catch(() => {
        if (active) setTokenMetadataStatus('error');
      });
    return () => {
      active = false;
    };
  }, [tokenMetadataRetryNonce]);

  // Asset prices from websocket index prices (symbol format: SPOT_{ASSET}_USDC)
  useEffect(() => {
    if (!streamAccountId) return;

    const wsUrlTemplate = import.meta.env.VITE_ORDERLY_INDEXPRICE_WS_URL as string | undefined;
    const defaultWsBase = 'wss://ws-evm.orderly.org/ws/stream/{account_id}';
    const wsUrl = wsUrlTemplate
      ? wsUrlTemplate.replace('{account_id}', streamAccountId)
      : defaultWsBase.replace('{account_id}', streamAccountId);
    if (!trackedTokensKey) return;

    const ws = new WebSocket(wsUrl);
    let pingInterval: number | null = null;
    let retryTimeout: number | null = null;
    const shortAccountId = streamAccountId.replace(/^0x/, '').slice(0, 16);
    const subscriptionId = `idxprices-${shortAccountId}`;
    const trackedTokenSet = new Set(trackedTokensKey.split(','));

    ws.onopen = () => {
      ws.send(JSON.stringify({ id: subscriptionId, event: 'subscribe', topic: 'indexprices' }));

      pingInterval = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ event: 'ping' }));
        }
      }, 10000);
    };

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);

        if (payload && typeof payload === 'object' && (payload as { event?: string }).event === 'ping') {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ event: 'pong' }));
          }
          return;
        }

        const entries = extractIndexPriceEntries(payload);
        if (entries.length === 0) return;

        setTokenPriceMap((prev) => {
          const next = new Map(prev);
          for (const entry of entries) {
            const match = entry.symbol.match(/^SPOT_(.+)_USDC$/);
            if (!match?.[1]) continue;
            const token = match[1];
            if (trackedTokenSet.has(token)) {
              next.set(token, entry.price);
            }
          }
          return next;
        });
      } catch {
        // ignore non-json messages
      }
    };

    ws.onclose = () => {
      retryTimeout = window.setTimeout(() => {
        setWsRetryNonce((prev) => prev + 1);
      }, 2000);
    };

    return () => {
      if (pingInterval !== null) {
        window.clearInterval(pingInterval);
      }
      if (retryTimeout !== null) {
        window.clearTimeout(retryTimeout);
      }
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ id: subscriptionId, event: 'unsubscribe', topic: 'indexprices' }));
      }
      ws.close();
    };
  }, [streamAccountId, trackedTokensKey, wsRetryNonce]);

  // Reset chain selection when expanded asset card changes
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSelectedDestinationChain(null);
      setDestinationChainSwitchError(null);
      setTransferTargetAccountId('');
    }, 0);
    return () => window.clearTimeout(timer);
  }, [expandedAssetCard]);

  // Auto-skip steps that have nothing to do
  useEffect(() => {
    if (!account || !selectedScope) return;
    if (account.pendingOrders.length === 0 && state.steps.cancel_orders === 'pending') {
      dispatch({ type: 'SKIP_STEP', step: 'cancel_orders', ...selectedScope });
    }
    if (account.positions.length === 0 && state.steps.close_positions === 'pending') {
      dispatch({ type: 'SKIP_STEP', step: 'close_positions', ...selectedScope });
    }
    if (account.unsettledPnl === 0 && state.steps.settle_pnl === 'pending') {
      dispatch({ type: 'SKIP_STEP', step: 'settle_pnl', ...selectedScope });
    }
    if (
      !hasNegativeUsdc(account)
      && isStepDone(state.steps.settle_pnl)
      && (state.steps.convert_negative_usdc === 'pending' || state.steps.convert_negative_usdc === 'error')
    ) {
      dispatch({ type: 'SKIP_STEP', step: 'convert_negative_usdc', ...selectedScope });
    }
    if ((account.holdings ?? []).every((h) => h.holding <= 0) && isStepDone(state.steps.convert_negative_usdc) && state.steps.withdraw === 'pending') {
      dispatch({ type: 'SKIP_STEP', step: 'withdraw', ...selectedScope });
    }
  }, [account, selectedScope, state.steps.cancel_orders, state.steps.close_positions, state.steps.settle_pnl, state.steps.convert_negative_usdc, state.steps.withdraw, dispatch]);

  // Auto-expand first actionable step
  useEffect(() => {
    if (!account) return;
    const nextExpandedStep = (() => {
      const order: OffboardingStep[] = ['cancel_orders', 'close_positions', 'settle_pnl', 'convert_negative_usdc', 'withdraw'];
      for (const step of order) {
        if (isStepEnabled(step, state.steps) && hasPendingAction(step, account)) {
          return step;
        }
      }
      return null;
    })();

    const timer = window.setTimeout(() => {
      setExpandedStep(nextExpandedStep);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [account, state.steps]);

  if (!account) return null;

  const stepConfigs = getSteps(account.type === 'sub');
  const negativeUsdcBalance = getNegativeUsdcBalance(account);
  const negativeUsdcCollateralMessage = getNegativeUsdcCollateralMessage(account);

  const dirtySubAccounts = (broker?.accounts ?? []).filter(
    (a) => a.type === 'sub' && (a.holdings.some((h) => h.holding > 0) || a.positions.length > 0),
  );

  function getWithdrawAmountInput(token: string, available: number): string {
    const amount = withdrawAmounts[token];
    if (amount?.source === 'manual') return amount.value;

    const tokenInfo = tokenInfoMap.get(token);
    if (withdrawAction === 'wallet' && tokenInfo && isValidTokenDecimals(tokenInfo.decimals)) {
      return formatFullWithdrawalAmount(available, tokenInfo.decimals);
    }
    return amount?.value ?? formatAmountInput(available);
  }

  function prepareWithdrawAction(token: string, action: 'wallet' | 'transfer', available: number) {
    setWithdrawToken(token);
    setWithdrawAction(action);
    setWithdrawAmounts((prev) => (
      prev[token]?.source === 'manual'
        ? prev
        : { ...prev, [token]: { value: formatAmountInput(available), source: 'auto' } }
    ));
  }

  function clearWithdrawAmount(token: string) {
    setWithdrawAmounts((prev) => {
      if (prev[token] === undefined) return prev;
      const next = { ...prev };
      delete next[token];
      return next;
    });
  }

  function getConvertOptions(): { items: ConvertOptionItem[]; targetUsdc: number; missingPriceTokens: string[] } {
    const targetUsdc = negativeUsdcBalance * (1 + CONVERT_COVER_BUFFER);
    const missingPriceTokens: string[] = [];
    const items = account!.holdings
      .filter((holding) => holding.token !== 'USDC' && getAvailableAmount(holding) > 0)
      .flatMap((holding) => {
        const available = getAvailableAmount(holding);
        const price = tokenPriceMap.get(holding.token) ?? 0;
        if (price <= 0) {
          missingPriceTokens.push(holding.token);
          return [];
        }

        const amount = Math.min(available, targetUsdc / price);
        const estimatedUsdc = amount * price;
        return [{
          token: holding.token,
          amount,
          available,
          price,
          estimatedUsdc,
          canCoverTarget: estimatedUsdc + 0.000001 >= negativeUsdcBalance,
        }];
      })
      .sort((a, b) => Number(b.canCoverTarget) - Number(a.canCoverTarget) || b.estimatedUsdc - a.estimatedUsdc || a.token.localeCompare(b.token));

    return {
      items,
      targetUsdc,
      missingPriceTokens,
    };
  }

  async function handleDestinationChainChange(value: string) {
    const nextChainId = value ? parseInt(value, 10) : null;
    setSelectedDestinationChain(nextChainId);
    setDestinationChainSwitchError(null);

    if (!nextChainId || state.chain !== 'evm' || !SUPPORTED_EVM_CHAIN_IDS.has(nextChainId) || chain?.id === nextChainId) {
      return;
    }

    const chainName = getChainLabel(nextChainId);
    setSwitchingDestinationChain(nextChainId);
    try {
      await withTimeout(
        switchChainAsync({ chainId: nextChainId }),
        SWITCH_CHAIN_TIMEOUT_MS,
        `Wallet switch to ${chainName} timed out. Close the wallet popup and try again.`,
      );
    } catch {
      setDestinationChainSwitchError(`Switch your wallet to ${chainName} before withdrawing. If the wallet popup is blank, close it and try again.`);
    } finally {
      setSwitchingDestinationChain(null);
    }
  }

  function getConfirmInfo(step: OffboardingStep): { title: string; description: string; details?: Array<{ label: string; value: string; subvalue?: string }>; items?: ReactNode[]; warning: string; warningType?: OdsAlertType } {
    switch (step) {
      case 'cancel_orders':
        return {
          title: 'Cancel all pending orders',
          description: `This will cancel ${account!.pendingOrders.length} pending order(s).`,
          items: account!.pendingOrders.map((o) => `${o.side} ${o.quantity} ${o.symbol.replace('PERP_', '').replace('_USDC', '')} @ ${formatNum(o.price, 2)}`),
          warning: 'This action cannot be undone. All pending orders will be cancelled immediately.',
          warningType: 'warning',
        };
      case 'close_positions':
        return {
          title: 'Close all positions',
          description: `This will place market orders to close ${account!.positions.length} position(s).`,
          items: account!.positions.map((p) => (
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
              <div className="grid min-w-0 grid-cols-[72px_72px_minmax(0,1fr)] items-center gap-2">
                <OdsBadge className="w-full" type={p.marginMode === 'ISOLATED' ? 'warning' : 'subtle'}>{formatMarginMode(p.marginMode)}</OdsBadge>
                <OdsBadge className="w-full" type={p.side.toLowerCase() === 'long' ? 'positive' : 'negative'}>{p.side}</OdsBadge>
                <span className="font-semibold text-[var(--fg-base)]">
                  {formatNum(p.quantity)} {p.symbol.replace('PERP_', '').replace('_USDC', '')}
                </span>
              </div>
              <span className={cn('shrink-0 font-medium', p.unrealizedPnl >= 0 ? 'text-[var(--fg-positive)]' : 'text-[var(--fg-negative)]')}>
                PnL: {formatUSD(p.unrealizedPnl)}
              </span>
            </div>
          )),
          warning: 'Positions will be closed at market price. Slippage may occur.',
          warningType: 'warning',
        };
      case 'settle_pnl':
        return {
          title: 'Settle pending PnL',
          description: account!.unsettledPnl !== 0 ? 'Finalize and convert your pending PnL into your account balance' : 'No unsettled PnL',
          warning: account!.type === 'main' ? 'This requires a wallet signature.' : 'This will settle PnL for this sub-account.',
          warningType: 'info',
        };
      case 'convert_negative_usdc': {
        const option = selectedConvertOption;
        if (!option) {
          return {
            title: 'Convert collateral to USDC',
            description: 'Select a collateral asset to convert.',
            warning: 'No collateral asset has been selected.',
            warningType: 'warning',
          };
        }

        return {
          title: `Convert ${option.token} to USDC`,
          description: `Cover ${formatNum(negativeUsdcBalance)} negative USDC before withdrawing.`,
          items: [
            <div className="grid gap-4">
              <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3">
                <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-surface-primary)] p-3">
                  <div className="text-xs text-[var(--fg-secondary)]">You convert</div>
                  <div className="mt-1 truncate text-lg font-semibold text-[var(--fg-base)]">{formatNum(option.amount)} {option.token}</div>
                  <div className="mt-1 text-xs text-[var(--fg-secondary)]">Available {formatNum(option.available)} {option.token}</div>
                </div>
                <div className="text-lg font-semibold text-[var(--fg-secondary)]" aria-hidden="true">→</div>
                <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-surface-primary)] p-3 text-right">
                  <div className="text-xs text-[var(--fg-secondary)]">Estimated receive</div>
                  <div className="mt-1 truncate text-lg font-semibold text-[var(--fg-base)]">{formatNum(option.estimatedUsdc)} USDC</div>
                  <div className="mt-1 text-xs text-[var(--fg-secondary)]">Need {formatNum(negativeUsdcBalance)} USDC</div>
                </div>
              </div>
              <div className="grid gap-2 text-xs text-[var(--fg-secondary)]">
                <div className="flex items-center justify-between gap-3">
                  <span>Rate</span>
                  <span className="font-medium text-[var(--fg-base)]">1 {option.token} = {formatNum(option.price)} USDC</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Minimum received</span>
                  <span className="font-medium text-[var(--fg-base)]">{formatNum(option.estimatedUsdc * (1 - DEFAULT_CONVERT_SLIPPAGE))} USDC</span>
                </div>
              </div>
            </div>,
          ],
          warning: option.canCoverTarget
            ? `Only ${option.token} will be converted. Other collateral assets will not be touched.`
            : `${option.token} alone is not estimated to fully cover the negative USDC balance. After this conversion, the step will remain until the rest is covered.`,
          warningType: option.canCoverTarget ? 'info' : 'warning',
        };
      }
      case 'withdraw': {
        const h = account!.holdings.find((h) => h.token === withdrawToken);
        const isTransfer = account!.type === 'sub' || withdrawAction === 'transfer';
        const availableAmount = h ? withdrawalAvailableMap.get(h.token) ?? 0 : 0;
        const rawAmount = h ? getWithdrawAmountInput(h.token, availableAmount) : '';
        const tokenInfo = h ? tokenInfoMap.get(h.token) : undefined;
        let displayAmount = rawAmount;
        if (!isTransfer && tokenInfo && isValidTokenDecimals(tokenInfo.decimals)) {
          try {
            displayAmount = normalizeWithdrawalAmount(rawAmount, tokenInfo.decimals);
          } catch {
            displayAmount = rawAmount;
          }
        }
        const mainAccountId = broker?.accounts.find((item) => item.type === 'main')?.accountId;
        const recipientDetail = isTransfer
          ? account!.type === 'sub'
            ? {
                value: 'Main account',
                subvalue: mainAccountId || undefined,
              }
            : {
                value: transferTargetAccountId || 'Target main account',
              }
          : {
              value: 'Connected wallet',
              subvalue: withdrawalReceiverAddress ?? undefined,
            };
        return {
          title: isTransfer ? `Transfer ${withdrawToken}` : `Withdraw ${withdrawToken}`,
          description: isTransfer ? '' : `Withdraw ${displayAmount} ${withdrawToken}.`,
          details: [
            { label: 'Amount', value: `${displayAmount} ${withdrawToken}` },
            ...(!isTransfer ? [{ label: 'Destination chain', value: selectedDestinationChainName || 'Selected chain' }] : []),
            { label: 'Recipient', ...recipientDetail },
          ],
          warning: isTransfer
            ? 'Please confirm transfer details before executing.'
            : 'This requires a wallet signature. Withdrawals may take a few minutes to process.',
          warningType: 'info',
        };
      }
    }
  }

  function handleExecuteClick(step: OffboardingStep, convertOption?: ConvertOptionItem) {
    if (step === 'convert_negative_usdc') {
      setSelectedConvertOption(convertOption ?? null);
    } else {
      setSelectedConvertOption(null);
    }

    if (account) {
      trackEvent('offboarding_execute_clicked', {
        step,
        component: 'StepProgress',
        account_id: account.accountId,
        account_type: account.type,
        broker_id: broker?.id ?? undefined,
        token: step === 'convert_negative_usdc' ? convertOption?.token : undefined,
      });
    }
    setConfirmingStep(step);
  }

  function showToast(origin: AccountScope, message: string, type: OdsAlertType = 'positive', label = 'Success') {
    setSuccessToast({ message, type, label, origin });
    if (successToastTimerRef.current) {
      window.clearTimeout(successToastTimerRef.current);
    }
    successToastTimerRef.current = window.setTimeout(() => {
      setSuccessToast(null);
      successToastTimerRef.current = null;
    }, 2600);
  }

  async function executeStep(step: OffboardingStep) {
    const currentAccount = account;
    const origin = selectedScope;
    if (currentAccount) {
      trackEvent('modal_closed', {
        modal: 'confirm_execute_step',
        step,
        component: 'StepProgress',
        account_id: currentAccount.accountId,
        account_type: currentAccount.type,
        broker_id: broker?.id ?? undefined,
        action: 'confirm',
      });
    }
    setConfirmingStep(null);
    if (!currentAccount || !origin) return;

    let selectedWithdrawAmountRaw: string | undefined;
    let selectedWithdrawAmountSource: WithdrawalAmountInput['source'] | undefined;
    let selectedConvertPlan: Array<{ token: string; amount: number }> | undefined;
    if (step === 'convert_negative_usdc') {
      if (!selectedConvertOption) {
        dispatch({ type: 'STEP_ERROR', step, error: 'Select a collateral asset to convert', ...origin });
        return;
      }

      selectedConvertPlan = [{ token: selectedConvertOption.token, amount: selectedConvertOption.amount }];
    }

    if (step === 'withdraw') {
      const holding = currentAccount.holdings.find((h) => h.token === withdrawToken);
      if (!holding || holding.holding <= 0) {
        dispatch({ type: 'STEP_ERROR', step, error: 'Please select an asset to withdraw', ...origin });
        return;
      }

      const availableAmount = withdrawalAvailableMap.get(holding.token) ?? 0;
      const amountInput = getWithdrawAmountInput(holding.token, availableAmount);
      const walletWithdrawal = currentAccount.type === 'main' && withdrawAction === 'wallet';
      const tokenInfo = tokenInfoMap.get(holding.token);
      if (walletWithdrawal) {
        if (tokenMetadataStatus !== 'success' || !tokenInfo || !isValidTokenDecimals(tokenInfo.decimals)) {
          dispatch({ type: 'STEP_ERROR', step, error: 'Token withdrawal metadata is unavailable', ...origin });
          return;
        }
        if (selectedDestinationChain === null || !tokenInfo.chain_details.some((detail) => Number(detail.chain_id) === selectedDestinationChain)) {
          dispatch({ type: 'STEP_ERROR', step, error: `${holding.token} is not supported on the selected destination chain`, ...origin });
          return;
        }
        if (!isDestinationChainForWallet(selectedDestinationChain, state.chain)) {
          dispatch({ type: 'STEP_ERROR', step, error: 'Destination chain does not match the connected wallet type', ...origin });
          return;
        }
      }
      const amountError = getAmountValidationError(amountInput, availableAmount, walletWithdrawal ? tokenInfo?.decimals : undefined);
      if (amountError) {
        dispatch({ type: 'STEP_ERROR', step, error: amountError, ...origin });
        return;
      }
      selectedWithdrawAmountRaw = amountInput.trim();
      selectedWithdrawAmountSource = withdrawAmounts[holding.token]?.source ?? 'auto';
    }

    if (step === 'withdraw' && withdrawAction === 'wallet' && state.chain === 'evm' && isAbstractWallet) {
      const destinationChainId = selectedDestinationChain ?? activeChainId;
      if (destinationChainId === undefined || !ABSTRACT_CHAIN_IDS.has(destinationChainId)) {
        const message = 'Abstract wallet withdrawals can only be sent to Abstract.';
        setDestinationChainSwitchError(message);
        dispatch({ type: 'STEP_ERROR', step, error: message, ...origin });
        return;
      }
    }

    if (!USE_MOCK && step === 'withdraw' && withdrawAction === 'wallet' && state.chain === 'evm' && isSupportedEvmDestinationChain(selectedDestinationChain) && chain?.id !== selectedDestinationChain) {
      const message = `Switch your wallet to ${getChainLabel(selectedDestinationChain)} before withdrawing.`;
      setDestinationChainSwitchError(message);
      dispatch({ type: 'STEP_ERROR', step, error: message, ...origin });
      return;
    }

    if (USE_MOCK) {
      dispatch({ type: 'START_STEP', step, ...origin });
      await new Promise((r) => setTimeout(r, 1500));
      const originStillSelected = isSameAccountScope(selectedScopeRef.current, origin);
      if (step === 'withdraw') {
        const holding = currentAccount.holdings.find((h) => h.token === withdrawToken);
        if (holding && selectedWithdrawAmountRaw !== undefined) {
          const tokenInfo = tokenInfoMap.get(holding.token);
          dispatch({
            type: 'WITHDRAW_ASSET',
            token: holding.token,
            amount: Number(selectedWithdrawAmountRaw),
            ...(withdrawAction === 'wallet' && selectedWithdrawAmountSource === 'auto' && tokenInfo && isValidTokenDecimals(tokenInfo.decimals)
              ? { fullWithdrawalDecimals: tokenInfo.decimals }
              : {}),
            ...origin,
          });
          if (originStillSelected) {
            setWithdrawToken('');
            clearWithdrawAmount(holding.token);
            setExpandedAssetCard(null);
          }
        }
      } else if (step === 'convert_negative_usdc') {
        if (!selectedConvertOption) return;
        const currentUsdcBalance = Math.min(
          currentAccount.usdcBalance,
          currentAccount.holdings.find((holding) => holding.token === 'USDC')?.holding ?? currentAccount.usdcBalance,
        );
        const nextUsdcBalance = currentUsdcBalance + selectedConvertOption.estimatedUsdc;
        const covered = nextUsdcBalance >= 0;
        const remainingCollateral = Math.max(
          (currentAccount.holdings.find((holding) => holding.token === selectedConvertOption.token)?.holding ?? 0) - selectedConvertOption.amount,
          0,
        );
        dispatch({
          type: 'APPLY_CONVERT_TO_USDC_RESULT',
          ...origin,
          token: selectedConvertOption.token,
          collateralHolding: remainingCollateral,
          usdcBalance: nextUsdcBalance,
        });
        dispatch({ type: covered ? 'COMPLETE_STEP' : 'CANCEL_STEP', step, ...origin });
        if (originStillSelected) setSelectedConvertOption(null);
        if (covered) {
          if (originStillSelected) {
            showToast(origin, 'Negative USDC covered. You can continue withdrawing.', 'positive', 'Success');
          }
        } else {
          if (originStillSelected) {
            showToast(origin, 'Collateral converted. Continue covering the remaining negative USDC.', 'warning', 'Partly covered');
          }
        }
      } else {
        dispatch({ type: 'COMPLETE_STEP', step, ...origin });
      }
      if (!(step === 'withdraw' && currentAccount.type === 'main' && withdrawAction === 'wallet')) {
        onStepComplete?.();
      }
      return;
    }

    const result = await executeLiveStep({
      step,
      withdrawToken,
      withdrawAction,
      selectedDestinationChain,
      transferTargetAccountId,
      withdrawAmountRaw: selectedWithdrawAmountRaw,
      withdrawAmountSource: selectedWithdrawAmountSource,
      convertPlan: selectedConvertPlan,
      convertSlippage: DEFAULT_CONVERT_SLIPPAGE,
    });

    if (!isSameAccountScope(selectedScopeRef.current, origin)) return;

    if (step === 'withdraw' && result === 'success') {
      if (withdrawToken) {
        clearWithdrawAmount(withdrawToken);
      }
      setWithdrawToken('');
      setExpandedAssetCard(null);
    }

    if (step === 'convert_negative_usdc' && (result === 'success' || result === 'partial' || result === 'unsettled')) {
      setSelectedConvertOption(null);
    }

    if (result === 'success') {
      showToast(
        origin,
        step === 'withdraw' && withdrawAction === 'wallet'
          ? 'Withdrawal request submitted.'
          : step === 'convert_negative_usdc'
          ? 'Negative USDC covered. You can continue withdrawing.'
          : 'Step completed successfully.',
        'positive',
        'Success',
      );
    } else if (result === 'partial') {
      showToast(origin, 'Collateral converted. Continue covering the remaining negative USDC.', 'warning', 'Partly covered');
    } else if (result === 'unsettled') {
      showToast(origin, 'Conversion submitted. Your balance is still settling - refresh to check before continuing.', 'info', 'Still settling');
    }
  }

  return (
    <div className="step-progress">
      {state.reopenedSteps.length > 0 && (
        <OdsAlert type="info" className="mb-3">New account activity</OdsAlert>
      )}

      {successToast && isSameAccountScope(successToast.origin, selectedScope) && (
        <OdsAlert
          type={successToast.type}
          className="fixed right-4 top-20 z-50 max-w-[min(440px,calc(100vw-24px))] shadow-[var(--shadow-popout)] max-sm:left-3 max-sm:right-3 max-sm:max-w-none"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="break-words text-sm text-[var(--fg-base)]">{successToast.message}</div>
              <div className="mt-1 text-xs">{successToast.label}</div>
            </div>
            <OdsButton variant="secondary" size="small" className="shrink-0 px-3" onClick={() => setSuccessToast(null)} aria-label="Close">
              Close
            </OdsButton>
          </div>
        </OdsAlert>
      )}

      {confirmingStep && (
        <ConfirmModal
          {...getConfirmInfo(confirmingStep)}
          confirmLabel="Execute"
          disabled={loadingStep === confirmingStep}
          onConfirm={() => executeStep(confirmingStep)}
          onCancel={() => {
            if (account) {
              trackEvent('modal_closed', {
                modal: 'confirm_execute_step',
                step: confirmingStep,
                component: 'StepProgress',
                account_id: account.accountId,
                account_type: account.type,
                broker_id: broker?.id ?? undefined,
                action: 'cancel',
              });
            }
            setConfirmingStep(null);
            if (confirmingStep === 'convert_negative_usdc') {
              setSelectedConvertOption(null);
            }
          }}
        />
      )}

      {stepConfigs.map((cfg, idx) => {
        const status = state.steps[cfg.key];
        const enabled = isStepEnabled(cfg.key, state.steps);
        const hasPending = hasPendingAction(cfg.key, account);
        const isLoading = loadingStep === cfg.key;
        const isLast = idx === stepConfigs.length - 1;
        const isError = status === 'error';
        const isSkipped = status === 'skipped';
        const isExpanded = expandedStep === cfg.key;
        const canExpand = hasPending && !isStepDone(status);

        // Asset steps use per-card buttons, so no global Execute button needed
        const usesInlineActions = cfg.key === 'withdraw' || cfg.key === 'convert_negative_usdc';
        const actionDisabled = !enabled || isLoading;
        const convertOptions = cfg.key === 'convert_negative_usdc' ? getConvertOptions() : null;

        return (
          <div key={cfg.key} className={`step-item ${status} ${isExpanded ? 'step-item--expanded' : ''}`}>
            <div className="step-indicator">
              <div className={`step-circle ${isSkipped ? 'completed' : status}`}>
                {isStepDone(status) ? '\u2713' : status === 'error' ? '!' : idx + 1}
              </div>
              {!isLast && <div className={`step-line ${isStepDone(status) ? 'completed' : ''}`} />}
            </div>
            <div className="step-body">
              <div
                className={`step-header ${canExpand ? 'step-header--clickable' : ''}`}
                onClick={() => canExpand && setExpandedStep(isExpanded ? null : cfg.key)}
              >
                <div className="step-content">
                  <div className="step-label">{cfg.label}</div>
                  <div className="step-sublabel">
                    {isSkipped
                      ? cfg.key === 'convert_negative_usdc' && hasNegativeUsdc(account)
                        ? 'Swap skipped; partial withdrawal is available'
                        : cfg.skippedLabel
                      : isLoading && state.stepProgress ? state.stepProgress : cfg.subLabel}
                  </div>
                </div>
                <div className="step-action">
                  {isStepDone(status) ? (
                    <span className={isSkipped ? 'step-skipped-label' : 'step-done'}>
                      Completed
                    </span>
                  ) : usesInlineActions ? (
                    isLoading ? <span className="step-loading-label">Processing...</span> : null
                  ) : (
                    <OdsButton
                      size="small"
                      className={cn(isError && 'bg-[var(--fg-negative)]')}
                      disabled={actionDisabled}
                      onClick={(e) => { e.stopPropagation(); handleExecuteClick(cfg.key); }}
                    >
                      {isLoading ? 'Processing...' : isError ? 'Retry' : 'Execute'}
                    </OdsButton>
                  )}
                  {canExpand && (
                    <span className={`step-chevron ${isExpanded ? 'step-chevron--open' : ''}`}>{'\u203A'}</span>
                  )}
                </div>
              </div>

              {/* Expanded content: inline data for each step */}
              {isExpanded && (
                <div className="step-expanded">
                  {cfg.key === 'cancel_orders' && (
                    account.pendingOrders.length > 0 ? (
                      <Table className="min-w-[560px]">
                          <TableHeader className="bg-[var(--bg-surface-secondary)]">
                            <TableRow className="border-[var(--border-subtle)] hover:bg-transparent">
                              <TableHead className="text-[var(--fg-secondary)]">Symbol</TableHead>
                              <TableHead className="text-[var(--fg-secondary)]">Side</TableHead>
                              <TableHead className="text-[var(--fg-secondary)]">Type</TableHead>
                              <TableHead className="text-right text-[var(--fg-secondary)]">Qty</TableHead>
                              <TableHead className="text-right text-[var(--fg-secondary)]">Price</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {account.pendingOrders.map((o) => (
                              <TableRow key={o.id} className="border-[var(--border-subtle)] hover:bg-[var(--bg-hover)]">
                                <TableCell className="font-semibold text-[var(--fg-base)]">{o.symbol.replace('PERP_', '').replace('_USDC', '')}</TableCell>
                                <TableCell><OdsBadge type={o.side.toLowerCase() === 'long' ? 'positive' : 'negative'}>{o.side}</OdsBadge></TableCell>
                                <TableCell className="text-[var(--fg-base)]">{o.type}</TableCell>
                                <TableCell className="text-right text-[var(--fg-base)]">{formatNum(o.quantity)}</TableCell>
                                <TableCell className="text-right text-[var(--fg-base)]">{formatNum(o.price, 2)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                    ) : null
                  )}

                  {cfg.key === 'close_positions' && (
                    <PositionTable positions={account.positions} />
                  )}

                  {cfg.key === 'settle_pnl' && account.unsettledPnl !== 0 && (
                    <div className={`step-pnl-value ${account.unsettledPnl >= 0 ? 'positive' : 'negative'}`}>
                      Unsettled: {account.unsettledPnl >= 0 ? '+' : ''}{formatUSDPrecise(account.unsettledPnl)}
                    </div>
                  )}

                  {cfg.key === 'convert_negative_usdc' && (
                    <div className="step-withdraw-section">
                      <OdsAlert type="warning">
                        This account has -{formatNum(negativeUsdcBalance)} USDC. Convert collateral to cover it, or skip this step and withdraw only the amount available under Orderly's collateral rules.
                      </OdsAlert>
                      <div className="flex justify-end">
                        <OdsButton
                          size="small"
                          variant="secondary"
                          disabled={!enabled || isLoading || !selectedScope}
                          onClick={() => {
                            if (!selectedScope) return;
                            dispatch({ type: 'SKIP_STEP', step: 'convert_negative_usdc', ...selectedScope });
                            setExpandedStep('withdraw');
                          }}
                        >
                          Skip swap and withdraw available
                        </OdsButton>
                      </div>
                      {convertOptions && convertOptions.missingPriceTokens.length > 0 && convertOptions.items.length === 0 && (
                        <OdsAlert type="info">
                          Waiting for index prices for {convertOptions.missingPriceTokens.join(', ')} before calculating the conversion amount.
                        </OdsAlert>
                      )}
                      {convertOptions && convertOptions.items.length > 0 && convertOptions.items.every((item) => !item.canCoverTarget) && (
                        <OdsAlert type="warning">
                          No single collateral asset is estimated to cover the full {formatNum(convertOptions.targetUsdc)} USDC target. Choose one asset to reduce the liability; this step will remain until the remaining negative USDC is covered.
                        </OdsAlert>
                      )}
                      {convertOptions && convertOptions.items.length === 0 && convertOptions.missingPriceTokens.length === 0 && (
                        <OdsAlert type="negative">
                          This account still has negative USDC, but no convertible collateral is available in the latest account data. Refresh account data; if this remains, contact support.
                        </OdsAlert>
                      )}
                      {convertOptions && convertOptions.items.length > 0 && (
                        <div className="asset-card asset-card--expanded">
                          <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                              <div>
                                <div className="text-sm font-semibold text-[var(--fg-base)]">Choose collateral asset</div>
                                <div className="mt-1 text-xs leading-5 text-[var(--fg-secondary)]">
                                  Needs cover: {formatNum(negativeUsdcBalance)} USDC. Each option converts only the selected asset. Amounts are calculated automatically with a {formatNum(CONVERT_COVER_BUFFER * 100, 2)}% buffer. Slippage tolerance: {formatNum(DEFAULT_CONVERT_SLIPPAGE * 100, 2)}%.
                                </div>
                              </div>
                            </div>
                            <div className="grid gap-2">
                              {convertOptions.items.map((item) => (
                                <div key={item.token} className="flex flex-col gap-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-secondary)] p-3 sm:flex-row sm:items-center sm:justify-between">
                                  <div className="flex items-center gap-2">
                                    {!failedTokenLogos.has(item.token) && (
                                      <img
                                        className="asset-card-token-logo"
                                        src={`https://oss.orderly.network/static/symbol_logo/${item.token}.png`}
                                        alt={`${item.token} logo`}
                                        loading="lazy"
                                        onError={() => {
                                          setFailedTokenLogos((prev) => {
                                            if (prev.has(item.token)) return prev;
                                            const next = new Set(prev);
                                            next.add(item.token);
                                            return next;
                                          });
                                        }}
                                      />
                                    )}
                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold text-[var(--fg-base)]">{item.token}</span>
                                        <OdsBadge type={item.canCoverTarget ? 'positive' : 'warning'}>{item.canCoverTarget ? 'Can fully cover' : 'Partial only'}</OdsBadge>
                                      </div>
                                      <div className="text-xs text-[var(--fg-secondary)]">Available {formatNum(item.available)} {item.token} / Index {formatUSD(item.price)}</div>
                                    </div>
                                  </div>
                                  <div className="flex flex-col gap-2 sm:items-end">
                                    <div className="text-sm text-[var(--fg-base)] sm:text-right">
                                      <div>Convert {formatNum(item.amount)} {item.token}</div>
                                      <div className="text-xs text-[var(--fg-secondary)]">Est. {formatNum(item.estimatedUsdc)} USDC</div>
                                    </div>
                                    <OdsButton
                                      size="small"
                                      disabled={!enabled || isLoading}
                                      onClick={() => handleExecuteClick('convert_negative_usdc', item)}
                                    >
                                      Convert {item.token}
                                    </OdsButton>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {cfg.key === 'withdraw' && (
                    <div className="step-withdraw-section">
                      {account.type === 'main' && dirtySubAccounts.length > 0 && (
                        <OdsAlert type="negative">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="text-sm leading-5 text-[var(--fg-base)]">
                              Sub-account {dirtySubAccounts.map((a) => a.name).join(', ')} contains active balances or positions. Please close or transfer these before proceeding.
                            </div>
                            <OdsButton
                              size="small"
                              className="shrink-0 bg-[var(--fg-negative)]"
                              onClick={() => {
                                trackEvent('account_selected', {
                                  account_id: dirtySubAccounts[0].accountId,
                                  account_type: dirtySubAccounts[0].type,
                                  broker_id: broker?.id ?? undefined,
                                });
                                dispatch({ type: 'SELECT_ACCOUNT', accountId: dirtySubAccounts[0].accountId });
                              }}
                            >
                              Go to {dirtySubAccounts[0].name}
                            </OdsButton>
                          </div>
                        </OdsAlert>
                      )}
                      {negativeUsdcCollateralMessage && (
                        <OdsAlert type="warning">
                          {negativeUsdcCollateralMessage}
                        </OdsAlert>
                      )}
                      {account.type === 'main' && tokenMetadataStatus === 'loading' && (
                        <OdsAlert type="info">Loading withdrawal metadata...</OdsAlert>
                      )}
                      {account.type === 'main' && tokenMetadataStatus === 'error' && (
                        <OdsAlert type="negative">
                          <div className="flex items-center justify-between gap-3">
                            <span>Withdrawal metadata could not be loaded. Wallet withdrawals are disabled.</span>
                            <OdsButton size="small" variant="secondary" onClick={() => setTokenMetadataRetryNonce((value) => value + 1)}>
                              Retry
                            </OdsButton>
                          </div>
                        </OdsAlert>
                      )}
                      <div className="asset-card-list">
                        {account.holdings.filter((h) => h.holding > 0).map((h) => {
                          const available = withdrawalAvailableMap.get(h.token) ?? 0;
                          const isCardExpanded = expandedAssetCard === h.token;
                          const allFrozen = available <= 0;
                          const tokenPrice = tokenPriceMap.get(h.token);
                          const estimatedUsdValue = tokenPrice !== undefined ? h.holding * tokenPrice : null;
                          const amountInput = getWithdrawAmountInput(h.token, available);
                          const tokenInfo = tokenInfoMap.get(h.token);
                          const walletMetadataSupported = tokenMetadataStatus === 'success'
                            && !!tokenInfo
                            && isValidTokenDecimals(tokenInfo.decimals)
                            && Array.isArray(tokenInfo.chain_details)
                            && tokenInfo.chain_details.some((detail) => isDestinationChainForWallet(detail.chain_id, state.chain));
                          const selectedChainDetail = tokenInfo?.chain_details.find((detail) => Number(detail.chain_id) === selectedDestinationChain);
                          const baseWalletAmountError = getAmountValidationError(amountInput, available, tokenInfo && isValidTokenDecimals(tokenInfo.decimals) ? tokenInfo.decimals : undefined);
                          const walletAmountError = baseWalletAmountError ?? (
                            tokenInfo && isValidTokenDecimals(tokenInfo.decimals) && selectedChainDetail
                              ? getWalletWithdrawalAmountError(amountInput, tokenInfo.decimals, tokenInfo.minimum_withdraw_amount, selectedChainDetail.withdrawal_fee)
                              : null
                          );
                          const transferAmountError = getAmountValidationError(amountInput, available);
                          const tokenCollateralMessage = h.token === 'USDC' ? null : getNegativeUsdcCollateralMessage(account, h.token);
                          const amountMessage = tokenCollateralMessage
                            ? `SDK max withdrawal: ${formatNum(available)} ${h.token}`
                            : `Available: ${formatNum(available)} ${h.token}`;
                          return (
                            <div key={h.token} className={`asset-card ${isCardExpanded ? 'asset-card--expanded' : ''}`}>
                              <div className="asset-card-header">
                                <div className="asset-card-meta">
                                  <div className="asset-card-info">
                                    <div className="asset-card-token-row">
                                      {!failedTokenLogos.has(h.token) && (
                                        <img
                                          className="asset-card-token-logo"
                                          src={`https://oss.orderly.network/static/symbol_logo/${h.token}.png`}
                                          alt={`${h.token} logo`}
                                          loading="lazy"
                                          onError={() => {
                                            setFailedTokenLogos((prev) => {
                                              if (prev.has(h.token)) return prev;
                                              const next = new Set(prev);
                                              next.add(h.token);
                                              return next;
                                            });
                                          }}
                                        />
                                      )}
                                      <span className="asset-card-token">{h.token}</span>
                                    </div>
                                    {h.frozen > 0 && (
                                      <div className="asset-card-amounts">
                                        <span className="asset-card-frozen">{formatNum(h.frozen)} frozen</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="asset-card-value-col">
                                  <span className="asset-card-holding">{formatNum(h.holding)}</span>
                                  <span className="asset-card-usd">{estimatedUsdValue !== null ? formatUSD(estimatedUsdValue) : '$--'}</span>
                                </div>
                                <div className="asset-card-actions">
                                  {account.type === 'sub' ? (
                                    <OdsButton
                                      size="small"
                                      variant="secondary"
                                      disabled={!enabled || isLoading || allFrozen}
                                      onClick={() => {
                                        if (isCardExpanded && withdrawAction === 'transfer') {
                                          setExpandedAssetCard(null);
                                        } else {
                                          prepareWithdrawAction(h.token, 'transfer', available);
                                          setExpandedAssetCard(h.token);
                                        }
                                      }}
                                    >
                                      Transfer
                                    </OdsButton>
                                  ) : (
                                    <>
                                      <OdsButton
                                        size="small"
                                        variant="secondary"
                                        disabled={!enabled || isLoading || allFrozen || !walletMetadataSupported}
                                        onClick={() => {
                                          if (isCardExpanded && expandedAssetCard === h.token && withdrawAction === 'wallet') {
                                            setExpandedAssetCard(null);
                                          } else {
                                            prepareWithdrawAction(h.token, 'wallet', available);
                                            setExpandedAssetCard(h.token);
                                          }
                                        }}
                                      >
                                        Withdraw
                                      </OdsButton>
                                      <OdsButton
                                        size="small"
                                        variant="secondary"
                                        disabled={!enabled || isLoading || allFrozen}
                                        onClick={() => {
                                          if (isCardExpanded && withdrawAction === 'transfer' && expandedAssetCard === h.token) {
                                            setExpandedAssetCard(null);
                                          } else {
                                            prepareWithdrawAction(h.token, 'transfer', available);
                                            setExpandedAssetCard(h.token);
                                          }
                                        }}
                                      >
                                        Transfer
                                      </OdsButton>
                                    </>
                                  )}
                                </div>
                              </div>
                              {allFrozen && (
                                <div className="asset-card-frozen-msg">No balance is currently withdrawable</div>
                              )}
                              {account.type === 'main' && tokenMetadataStatus === 'success' && !walletMetadataSupported && (
                                <div className="asset-card-frozen-msg">Wallet withdrawal is unsupported for this token.</div>
                              )}
                              {isCardExpanded && !allFrozen && (
                                <div className="asset-card-options">
                                  {tokenCollateralMessage && (
                                    <OdsAlert type="warning" className="mb-3">
                                      {tokenCollateralMessage}
                                    </OdsAlert>
                                  )}
                                  {withdrawAction === 'wallet' && (() => {
                                    const allAvailableChains = sortChainOptions((tokenInfo?.chain_details ?? [])
                                      .filter((detail) => isDestinationChainForWallet(detail.chain_id, state.chain))
                                      .map((detail) => ({
                                      chainId: String(detail.chain_id),
                                      label: getChainLabel(detail.chain_id),
                                      fee: detail.withdrawal_fee,
                                    })));
                                    const availableChains = isAbstractWallet
                                      ? allAvailableChains.filter((c) => ABSTRACT_CHAIN_IDS.has(Number(c.chainId)))
                                      : allAvailableChains;
                                    const selectedChainAvailable = selectedDestinationChain !== null
                                      && availableChains.some((c) => Number(c.chainId) === selectedDestinationChain);
                                    const chainSelectError = destinationChainSelectError
                                      ?? (selectedDestinationChain && !selectedChainAvailable ? `${h.token} is not available for withdrawal to ${getChainLabel(selectedDestinationChain)}.` : undefined);
                                    return (
                                      <div className="grid gap-3">
                                        <div className="grid gap-3 md:grid-cols-[minmax(160px,1fr)_minmax(220px,1.2fr)_auto] md:items-start">
                                          <OdsInput
                                            type="text"
                                            inputMode="decimal"
                                            label="Amount"
                                            value={amountInput}
                                            onChange={(e) => setWithdrawAmounts((prev) => ({ ...prev, [h.token]: { value: e.target.value, source: 'manual' } }))}
                                            disabled={isLoading}
                                            suffix={h.token}
                                            message={amountMessage}
                                            error={walletAmountError ?? undefined}
                                          />
                                          <OdsSelect
                                            label="Destination chain"
                                            value={selectedDestinationChain ?? ''}
                                            onChange={(e) => void handleDestinationChainChange(e.target.value)}
                                            disabled={isLoading || switchingDestinationChain !== null || isAbstractWallet || !walletMetadataSupported}
                                            message={destinationChainSelectMessage}
                                            error={chainSelectError}
                                          >
                                            <option value="">Select chain...</option>
                                            {availableChains.map((c) => (
                                              <option key={c.chainId} value={c.chainId}>
                                                {c.label}{c.fee > 0 ? ` (fee: ${c.fee})` : ''}
                                              </option>
                                            ))}
                                          </OdsSelect>
                                          <OdsButton
                                            className="self-end"
                                            disabled={isLoading || switchingDestinationChain !== null || !selectedDestinationChain || destinationChainNeedsWalletSwitch || !selectedChainAvailable || !!walletAmountError || !walletMetadataSupported}
                                            onClick={() => {
                                              setWithdrawToken(h.token);
                                              handleExecuteClick('withdraw');
                                            }}
                                          >
                                            Confirm withdrawal
                                          </OdsButton>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                  {withdrawAction === 'transfer' && account.type === 'sub' && (
                                    <div className="grid gap-3 md:grid-cols-[minmax(160px,1fr)_auto] md:items-start">
                                      <OdsInput
                                        type="text"
                                        inputMode="decimal"
                                        label="Amount"
                                        value={amountInput}
                                        onChange={(e) => setWithdrawAmounts((prev) => ({ ...prev, [h.token]: { value: e.target.value, source: 'manual' } }))}
                                        disabled={isLoading}
                                        suffix={h.token}
                                        message={amountMessage}
                                          error={transferAmountError ?? undefined}
                                      />
                                      <div className="asset-card-button-row self-end">
                                        <p className="transfer-footnote">
                                          Transfer to main account.
                                        </p>
                                        <OdsButton
                                          disabled={isLoading || !!transferAmountError}
                                          onClick={() => {
                                            setWithdrawToken(h.token);
                                            handleExecuteClick('withdraw');
                                          }}
                                        >
                                          Confirm transfer
                                        </OdsButton>
                                      </div>
                                    </div>
                                  )}
                                  {withdrawAction === 'transfer' && account.type === 'main' && (
                                    <div className="grid gap-3 md:grid-cols-[minmax(160px,1fr)_minmax(260px,1.3fr)]">
                                      <OdsInput
                                        type="text"
                                        inputMode="decimal"
                                        label="Amount"
                                        value={amountInput}
                                        onChange={(e) => setWithdrawAmounts((prev) => ({ ...prev, [h.token]: { value: e.target.value, source: 'manual' } }))}
                                        disabled={isLoading}
                                        suffix={h.token}
                                        message={amountMessage}
                                          error={transferAmountError ?? undefined}
                                      />
                                      <OdsInput
                                        type="text"
                                        label="Target account ID"
                                        placeholder="0x..."
                                        value={transferTargetAccountId}
                                        onChange={(e) => setTransferTargetAccountId(e.target.value)}
                                        disabled={isLoading}
                                        error={trimmedTransferTargetAccountId && !isTransferTargetAccountIdValid ? 'Invalid format. Please check and try again.' : undefined}
                                      />
                                      <div className="asset-card-button-row md:col-span-2">
                                        <p className="transfer-footnote">
                                          Note: You can only transfer to whitelisted main account(s).
                                        </p>
                                        <OdsButton
                                          disabled={isLoading || !!transferAmountError || !isTransferTargetAccountIdValid}
                                          onClick={() => {
                                            setWithdrawToken(h.token);
                                            handleExecuteClick('withdraw');
                                          }}
                                        >
                                          Confirm transfer
                                        </OdsButton>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {state.stepError && state.steps[stepConfigs.find((s) => state.steps[s.key] === 'error')?.key ?? 'cancel_orders'] === 'error' && (
        <OdsAlert type="negative" className="mt-3">{state.stepError}</OdsAlert>
      )}
    </div>
  );
}
